import {
    AxleDialog,
    AxleDialogProps,
    AxleDialogTitle,
    TransitionComponent,
} from '@/components/layout/AxleDialog';
import { AxleErrorBoundary } from '@/fetching/errorBoundaries';
import { AxleSuspense, PAGE_BODY_SKELETON_HEIGHT } from '@/fetching/suspense';
import { ReturnComponent } from '@/utils/reactTypes';
import { Dialog, styled, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/styles';
import { FC, ReactElement, useCallback, useState } from 'react';

const StyledDialog = styled(Dialog)`
    & .MuiPaper-root {
        max-width: fit-content;
    }
`;

type ChildrenProps = {
    onClose(): void;
};

export type ManagedAxleDialogBaseProps = {
    onClose(): void;
    fullScreen?: boolean;
    title: string;
    hideCloseButton?: boolean;
    className?: string;
    children: (props: ChildrenProps) => ReactElement | null;
};

export const ManagedAxleDialogBase: FC<ManagedAxleDialogBaseProps> = (props) => {
    const {
        fullScreen,
        onClose: onTransitionExited,
        children,
        className,
        hideCloseButton,
        title,
    } = props;
    const [isOpen, setIsOpen] = useState(true);
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('xs')) || fullScreen;

    const onClose = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    return (
        <StyledDialog
            TransitionComponent={TransitionComponent}
            fullWidth
            fullScreen={isFullScreen}
            className={className}
            open={isOpen}
            onTransitionExited={onTransitionExited}
            onClose={onClose}
        >
            <AxleDialogTitle
                defaultPadding
                title={title}
                close={hideCloseButton ? null : onClose}
            />
            <AxleErrorBoundary>
                <AxleSuspense skeletonHeight={PAGE_BODY_SKELETON_HEIGHT} skeletonPadding={2}>
                    {children({ onClose })}
                </AxleSuspense>
            </AxleErrorBoundary>
        </StyledDialog>
    );
};

export type ManagedAxleDialogProps = {
    onClose: () => void;
    children: (props: { close: () => void }) => ReturnComponent;
};

/**
 * Extension of AxleDialog that is used by the DialogManager, which handles opening and closing
 * the dialog as invoked by anyone using the `openDialog` function provided by the `useDialog`
 * context hook.
 *
 * The primary benefit of this wrapper over AxleDialog alone is that the dialog is able to be
 * mounted, animate in, animate out when the dialog is closed, and then be automatically unmounted
 * when the transition completes.
 *
 * The outer component that is invoking the dialog does not need to provide/handle any state about
 * the dialog itself, only provide the custom props that are needed by the dialog's content.
 */
export function ManagedAxleDialog<PropsT>({
    onClose,
    children,
    ...rest
}: PropsT & ManagedAxleDialogProps & Omit<AxleDialogProps, 'children' | 'isOpen' | 'close'>) {
    const [isOpen, setIsOpen] = useState(true);

    const close = useCallback(() => setIsOpen(false), [setIsOpen]);
    return (
        <AxleDialog {...rest} isOpen={isOpen} close={close} onTransitionExited={onClose}>
            {children({ close })}
        </AxleDialog>
    );
}

export type ManagedDialogProps<PropsT> = PropsT & {
    dataTestId?: string;
    onClose: () => void;
    wrapClose?: (close: () => void) => () => void;
    children: (props: { close: () => void; wrappedClose?: () => void }) => ReturnComponent;
} & Omit<AxleDialogProps, 'children' | 'isOpen' | 'close' | 'title'>;

/**
 * Extension of Dialog that is used by the DialogManager, which handles opening and closing
 * the dialog as invoked by anyone using the `openDialog` function provided by the `useDialog`
 * context hook.
 *
 * The primary benefit of this wrapper over the MUI Dialog alone is that the dialog is able to be
 * mounted, animate in, animate out when the dialog is closed, and then be automatically unmounted
 * when the transition completes.
 *
 * The outer component that is invoking the dialog does not need to provide/handle any state about
 * the dialog itself, only provide the custom props that are needed by the dialog's content.
 */
export function ManagedDialog<PropsT>({
    dataTestId,
    onClose,
    children,
    wrapClose,
    ...rest
}: ManagedDialogProps<PropsT>) {
    const [isOpen, setIsOpen] = useState(true);

    const close = () => setIsOpen(false);
    const wrappedClose = wrapClose?.(close);

    return (
        <Dialog
            data-test-id={dataTestId}
            // Overridable props.
            TransitionComponent={TransitionComponent}
            // Overrides
            {...rest}
            // Non-overridable props.
            open={isOpen}
            onClose={wrappedClose ?? close}
            onTransitionExited={onClose}
        >
            {children({ close, wrappedClose })}
        </Dialog>
    );
}
